import type {
  Credentials,
  MatchDayResponse,
  MatchDaysResponse,
  PeriodType,
  PlayerOpResponse,
  PlayersResponse,
  Registration,
  SaveMatchDayResponse,
  Session,
  StandingsResponse,
  WireMatchDay,
  WireResponse
} from './types';

export type Transport = (url: string, init: RequestInit) => Promise<Response>;

export const SERVER_ERROR_PREFIX = 'Wystąpił błąd serwera';

export function normaliseMessages(raw: unknown): string[] {
  if (raw === undefined || raw === null) {
    return [];
  }
  const flat = Array.isArray(raw) ? raw.flat(Infinity) : [raw];
  return flat.filter((item) => item !== undefined && item !== null).map(String);
}

export class Api {
  private readonly baseUrl: string;

  private readonly transport: Transport;

  constructor(baseUrl: string, transport: Transport) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.transport = transport;
  }

  login(credentials: Credentials): Promise<Session> {
    return this.postJson<Session>('/login', credentials);
  }

  register(registration: Registration): Promise<WireResponse> {
    return this.postJson<WireResponse>('/register', registration);
  }

  logout(): Promise<WireResponse> {
    return this.send<WireResponse>('/logout', 'GET');
  }

  session(): Promise<Session> {
    return this.send<Session>('/is-logged-in', 'GET');
  }

  activePlayers(): Promise<PlayersResponse> {
    return this.send<PlayersResponse>('/get-all-active-users', 'GET');
  }

  allPlayers(): Promise<PlayersResponse> {
    return this.send<PlayersResponse>('/get-all-users', 'GET');
  }

  matchDays(page: { limit?: number; before?: string } = {}): Promise<MatchDaysResponse> {
    const query = new URLSearchParams();
    if (page.limit !== undefined) {
      query.set('limit', String(page.limit));
    }
    if (page.before) {
      query.set('before', page.before);
    }
    const suffix = query.size > 0 ? `?${query}` : '';
    return this.send<MatchDaysResponse>(`/match-days${suffix}`, 'GET');
  }

  matchDay(id: string): Promise<MatchDayResponse> {
    return this.send<MatchDayResponse>(`/match-days/${encodeURIComponent(id)}`, 'GET');
  }

  removeMatchDay(id: string): Promise<WireResponse> {
    return this.send<WireResponse>(`/match-days/${encodeURIComponent(id)}`, 'DELETE');
  }

  updateMatchDay(id: string, matchDay: WireMatchDay): Promise<SaveMatchDayResponse> {
    return this.putJson<SaveMatchDayResponse>(
      `/match-days/${encodeURIComponent(id)}`,
      matchDay
    );
  }

  saveMatchDay(matchDay: WireMatchDay): Promise<SaveMatchDayResponse> {
    return this.postJson<SaveMatchDayResponse>('/save-match-day', matchDay);
  }

  standings(type: PeriodType, value: number): Promise<StandingsResponse> {
    return this.postJson<StandingsResponse>('/get-summarized-results', { type, value });
  }

  sendDigest(): Promise<WireResponse> {
    return this.send<WireResponse>('/send-email', 'GET');
  }

  setActive(short: string, active: boolean): Promise<PlayerOpResponse> {
    return this.send<PlayerOpResponse>(`/user/${short}/${active ? 'activate' : 'deactivate'}`, 'GET');
  }

  setAdmin(short: string, admin: boolean): Promise<PlayerOpResponse> {
    return this.send<PlayerOpResponse>(`/user/${short}/${admin ? 'set-admin' : 'unset-admin'}`, 'GET');
  }

  removePlayer(short: string): Promise<PlayerOpResponse> {
    return this.send<PlayerOpResponse>(`/user/${short}/remove`, 'DELETE');
  }

  private postJson<T extends WireResponse>(path: string, payload: unknown): Promise<T> {
    return this.envelope<T>(path, 'POST', payload);
  }

  private putJson<T extends WireResponse>(path: string, payload: unknown): Promise<T> {
    return this.envelope<T>(path, 'PUT', payload);
  }

  private envelope<T extends WireResponse>(
    path: string,
    method: 'POST' | 'PUT',
    payload: unknown
  ): Promise<T> {
    const body = new FormData();
    body.append('json', JSON.stringify(payload));
    return this.request<T>(path, { method, body });
  }

  private send<T extends WireResponse>(path: string, method: 'GET' | 'DELETE'): Promise<T> {
    return this.request<T>(path, { method });
  }

  private async request<T extends WireResponse>(path: string, init: RequestInit): Promise<T> {
    let payload: unknown;
    try {
      const response = await this.transport(`${this.baseUrl}${path}`, {
        ...init,
        credentials: 'include'
      });
      payload = await response.json();
    } catch (cause) {
      throw new Error(
        `${SERVER_ERROR_PREFIX}: ${cause instanceof Error ? cause.message : String(cause)}`,
        { cause }
      );
    }
    const body = (payload ?? {}) as Record<string, unknown>;
    return {
      ...body,
      statusCode: typeof body.statusCode === 'number' ? body.statusCode : 0,
      messages: normaliseMessages(body.messages ?? body.message)
    } as T;
  }
}
